<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NotificationsAPI from 'dashboard/api/notifications';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import AssignmentTransferRequestsAPI from 'dashboard/api/aceleres/assignmentTransferRequests';
import ContactOwnershipRequestsAPI from 'dashboard/api/aceleres/contactOwnershipRequests';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const emit = defineEmits(['stateChange']);
const { t } = useI18n();
const route = useRoute();
const transferRequests = ref([]);
const resolvedTransferRequests = ref([]);
const selectedStatus = ref('pending');
const transferRequestsLoading = ref(false);
const transferRequestsError = ref(false);
const expandedTransferRequestId = ref(null);
const decidingTransferRequestId = ref(null);
const markingRequestReadId = ref(null);
const deletingRequestNotificationId = ref(null);
const rejectionTransferRequest = ref(null);
const rejectionReason = ref('');
const accountId = computed(() => Number(route.params.accountId));

const statusFilters = [
  { key: 'pending', label: 'INBOX.REQUESTS.FILTER_PENDING' },
  { key: 'approved', label: 'INBOX.REQUESTS.FILTER_APPROVED' },
  { key: 'rejected', label: 'INBOX.REQUESTS.FILTER_REJECTED' },
  { key: 'all', label: 'INBOX.REQUESTS.FILTER_ALL' },
];

const visibleTransferRequests = computed(() =>
  selectedStatus.value === 'all'
    ? transferRequests.value
    : transferRequests.value.filter(
        request => request.status === selectedStatus.value
      )
);

const pendingCount = computed(
  () =>
    transferRequests.value.filter(request => request.status === 'pending')
      .length
);
const unreadCount = computed(
  () =>
    transferRequests.value.filter(
      request => request.notification_id && !request.notification_read_at
    ).length
);

const pendingSummary = computed(() => {
  const pendingRequests = transferRequests.value.filter(
    request => request.status === 'pending'
  );
  const total = pendingRequests.length;
  const atendimento = pendingRequests.filter(
    request => request.kind === 'atendimento'
  ).length;
  const autorizacao = pendingRequests.filter(
    request => request.kind === 'ownership'
  ).length;
  const carteira = total - atendimento - autorizacao;
  const parts = [];
  if (atendimento) parts.push(`${atendimento} de atendimento`);
  if (autorizacao) parts.push(`${autorizacao} de autorização`);
  if (carteira) parts.push(`${carteira} de carteira`);

  return parts.join(' · ');
});

const countForStatus = status =>
  status === 'all'
    ? transferRequests.value.length
    : transferRequests.value.filter(request => request.status === status)
        .length;

const statusKeys = {
  pending: 'INBOX.REQUESTS.STATUS_PENDING',
  approved: 'INBOX.REQUESTS.STATUS_APPROVED',
  rejected: 'INBOX.REQUESTS.STATUS_REJECTED',
};

const statusClasses = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

let transferRequestFetchSequence = 0;
let transferRequestPollId = null;

// `id` se repete entre as duas tabelas (são sequências independentes), então
// tudo que identifica uma linha na tela precisa da combinação kind+id.
const requestKey = request => `${request?.kind}-${request?.id}`;

const emitState = () => {
  emit('stateChange', {
    count: transferRequests.value.length,
    unreadCount: unreadCount.value,
    loading: transferRequestsLoading.value,
    error: transferRequestsError.value,
  });
};

const fetchTransferRequests = async () => {
  transferRequestFetchSequence += 1;
  const fetchSequence = transferRequestFetchSequence;
  transferRequestsLoading.value = true;
  emitState();

  try {
    // As aprovações nativas e a autorização registrada no Laravel aparecem
    // juntas nesta central; cada domínio mantém sua persistência e decisão.
    const [carteirizacao, atendimento, ownership] = await Promise.all([
      ConversationTransferRequestsAPI.team(),
      AssignmentTransferRequestsAPI.team(),
      ContactOwnershipRequestsAPI.team(),
    ]);
    if (fetchSequence !== transferRequestFetchSequence) return;

    const merged = [
      ...(carteirizacao?.data?.payload || []),
      ...(atendimento?.data?.payload || []),
      ...(ownership?.data?.payload || []),
    ];
    // Mais recentes primeiro, sem depender da ordem em que as duas chamadas
    // voltaram.
    merged.sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    const requestKeys = new Set(merged.map(requestKey));
    const recentDecisions = resolvedTransferRequests.value.filter(
      request => !requestKeys.has(requestKey(request))
    );
    transferRequests.value = [...merged, ...recentDecisions].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    transferRequestsError.value = false;
  } catch {
    if (fetchSequence === transferRequestFetchSequence) {
      transferRequestsError.value = true;
    }
  } finally {
    if (fetchSequence === transferRequestFetchSequence) {
      transferRequestsLoading.value = false;
      emitState();
    }
  }
};

const fetchPendingTransferRequests = async () => {
  transferRequestFetchSequence += 1;
  const fetchSequence = transferRequestFetchSequence;

  try {
    const [carteirizacao, atendimento, ownership] = await Promise.all([
      ConversationTransferRequestsAPI.pending(),
      AssignmentTransferRequestsAPI.pending(),
      ContactOwnershipRequestsAPI.pending(),
    ]);
    if (fetchSequence !== transferRequestFetchSequence) return;

    const pendingRequests = [
      ...(carteirizacao?.data?.payload || []),
      ...(atendimento?.data?.payload || []),
      ...(ownership?.data?.payload || []),
    ];
    const pendingKeys = new Set(pendingRequests.map(requestKey));
    const previouslyPending = transferRequests.value.filter(
      request => request.status === 'pending'
    );

    if (
      previouslyPending.some(request => !pendingKeys.has(requestKey(request)))
    ) {
      await fetchTransferRequests();
      return;
    }

    const previousDecisions = transferRequests.value.filter(
      request => request.status !== 'pending'
    );
    transferRequests.value = [...pendingRequests, ...previousDecisions].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
    transferRequestsError.value = false;
    emitState();
  } catch {
    if (fetchSequence === transferRequestFetchSequence) {
      transferRequestsError.value = true;
      emitState();
    }
  }
};

const rememberResolvedTransferRequest = request => {
  if (!request || request.status === 'pending') return;

  const key = requestKey(request);
  resolvedTransferRequests.value = [
    request,
    ...resolvedTransferRequests.value.filter(
      existing => requestKey(existing) !== key
    ),
  ].slice(0, 5);
  transferRequests.value = [
    request,
    ...transferRequests.value.filter(existing => requestKey(existing) !== key),
  ].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  emitState();
};

const markRequestNotificationAsRead = async request => {
  if (
    !request.notification_id ||
    request.notification_read_at ||
    markingRequestReadId.value
  ) {
    return;
  }

  markingRequestReadId.value = request.notification_id;
  try {
    await NotificationsAPI.update(request.notification_id, {});
    transferRequestFetchSequence += 1;
    transferRequestsLoading.value = false;
    request.notification_read_at = new Date().toISOString();
    emitState();
  } catch {
    // A read-state failure does not block a decision; the unread highlight remains.
  } finally {
    markingRequestReadId.value = null;
  }
};

const toggleRequestReadState = async request => {
  const notificationId = request.notification_id;
  if (!notificationId || markingRequestReadId.value) return;

  markingRequestReadId.value = notificationId;
  try {
    if (request.notification_read_at) {
      await NotificationsAPI.unRead(notificationId);
      request.notification_read_at = null;
      useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD'));
    } else {
      await NotificationsAPI.update(notificationId, {});
      request.notification_read_at = new Date().toISOString();
      useAlert(t('INBOX.ALERTS.MARK_AS_READ'));
    }
    transferRequestFetchSequence += 1;
    transferRequestsLoading.value = false;
    emitState();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX.FOLLOWUPS.MARK_READ_ERROR')
    );
  } finally {
    markingRequestReadId.value = null;
  }
};

const deleteRequestNotification = async request => {
  const notificationId = request.notification_id;
  if (!notificationId || deletingRequestNotificationId.value) return;

  deletingRequestNotificationId.value = notificationId;
  try {
    await NotificationsAPI.delete(notificationId);
    request.notification_id = null;
    transferRequestFetchSequence += 1;
    transferRequestsLoading.value = false;
    request.notification_read_at = null;
    emitState();
    useAlert(t('INBOX.ALERTS.DELETE'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INBOX.FOLLOWUPS.LOAD_ERROR'));
  } finally {
    deletingRequestNotificationId.value = null;
  }
};

const refreshTransferRequestsWhenVisible = () => {
  if (document.visibilityState === 'visible') fetchTransferRequests();
};

const toggleTransferRequestDetails = async request => {
  const key = requestKey(request);
  const isExpanding = expandedTransferRequestId.value !== key;
  expandedTransferRequestId.value = isExpanding ? key : null;
  if (isExpanding) await markRequestNotificationAsRead(request);
};

const formatTransferRequestDate = createdAt => {
  if (!createdAt) return 'Data não informada';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(createdAt));
};

// Cada tipo tem seu próprio endpoint: um grava o dono do lead, o outro o
// responsável pelo atendimento. Escolher pela `kind` evita aprovar no fluxo
// errado.
const apiFor = request => {
  if (request?.kind === 'ownership') return ContactOwnershipRequestsAPI;
  if (request?.kind === 'atendimento') return AssignmentTransferRequestsAPI;
  return ConversationTransferRequestsAPI;
};

const labelFor = request => {
  if (request?.kind === 'ownership') return 'Autorização de atendimento';
  if (request?.kind === 'atendimento') return 'Transferência de atendimento';
  return 'Alteração de carteirização';
};

const approveTransferRequest = async request => {
  decidingTransferRequestId.value = requestKey(request);

  try {
    await markRequestNotificationAsRead(request);
    const response = await apiFor(request).approve(request.id);
    rememberResolvedTransferRequest(response?.data?.payload);
    useAlert(`${labelFor(request)} aprovada`);
    await fetchTransferRequests();
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível aprovar');
  } finally {
    decidingTransferRequestId.value = null;
  }
};

const openRejectTransferRequestModal = async request => {
  await markRequestNotificationAsRead(request);
  rejectionTransferRequest.value = request;
  rejectionReason.value = '';
};

const closeRejectTransferRequestModal = () => {
  if (decidingTransferRequestId.value) return;

  rejectionTransferRequest.value = null;
  rejectionReason.value = '';
};

const rejectTransferRequest = async () => {
  const request = rejectionTransferRequest.value;
  if (!request) return;

  decidingTransferRequestId.value = requestKey(request);

  try {
    await markRequestNotificationAsRead(request);
    const response = await apiFor(request).reject(
      request.id,
      rejectionReason.value.trim()
    );
    rememberResolvedTransferRequest(response?.data?.payload);
    useAlert(`${labelFor(request)} recusada`);
    rejectionTransferRequest.value = null;
    rejectionReason.value = '';
    await fetchTransferRequests();
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível recusar');
  } finally {
    decidingTransferRequestId.value = null;
  }
};

const onTransferRequestRealtimeUpdate = payload => {
  if (Number(payload?.account_id) !== accountId.value) return;

  // O broadcast não diz de qual fila veio; comparar só pelo sufixo do id
  // fecharia o card errado quando as duas tabelas coincidissem. Fecha apenas
  // quando o id bate em qualquer um dos dois tipos e a decisão já saiu.
  const decidedKeys = [
    `carteirizacao-${payload?.request_id}`,
    `atendimento-${payload?.request_id}`,
  ];
  if (
    decidedKeys.includes(expandedTransferRequestId.value) &&
    payload?.status !== 'pending'
  ) {
    expandedTransferRequestId.value = null;
  }

  fetchTransferRequests();
};

onMounted(() => {
  emitter.on(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  emitter.on(
    BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  emitter.on(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, fetchTransferRequests);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchTransferRequests);
  window.addEventListener('focus', fetchTransferRequests);
  document.addEventListener(
    'visibilitychange',
    refreshTransferRequestsWhenVisible
  );
  transferRequestPollId = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      fetchPendingTransferRequests();
    }
  }, 10000);
  fetchTransferRequests();
});

onBeforeUnmount(() => {
  emitter.off(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  emitter.off(
    BUS_EVENTS.ACELERES_ATENDIMENTO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  transferRequestFetchSequence += 1;
  emitter.off(BUS_EVENTS.INBOX_NOTIFICATION_UPDATED, fetchTransferRequests);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchTransferRequests);
  window.removeEventListener('focus', fetchTransferRequests);
  document.removeEventListener(
    'visibilitychange',
    refreshTransferRequestsWhenVisible
  );
  window.clearInterval(transferRequestPollId);
});
</script>

<template>
  <section
    v-show="transferRequests.length || transferRequestsError"
    data-testid="portfolio-transfer-requests-panel"
    class="shrink-0 border-b border-n-weak bg-n-surface-2 px-4 py-5 md:px-5"
  >
    <div class="mb-4 flex items-start justify-between gap-3">
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="i-lucide-clipboard-check mt-0.5 size-10 flex-shrink-0 rounded-xl bg-[#d9fdd3] p-2 text-[#15603e] dark:bg-[rgba(21,96,62,0.25)] dark:text-[#86efac]"
          aria-hidden="true"
        />
        <div class="min-w-0">
          <p
            class="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#15603e] dark:text-[#86efac]"
          >
            {{ $t('INBOX.REQUESTS.APPROVALS_LABEL') }}
          </p>
          <h2 class="mt-1 text-lg font-semibold leading-tight text-n-slate-12">
            {{ $t('INBOX.REQUESTS.APPROVAL_CENTER') }}
          </h2>
          <p class="mt-1 text-xs text-n-slate-10">
            <template v-if="pendingCount">
              {{
                $t(
                  pendingCount === 1
                    ? 'INBOX.REQUESTS.PENDING_COUNT_ONE'
                    : 'INBOX.REQUESTS.PENDING_COUNT',
                  { count: pendingCount }
                )
              }}
            </template>
            <template v-else>
              {{ $t('INBOX.REQUESTS.TEAM_DESCRIPTION') }}
            </template>
          </p>
          <p v-if="pendingSummary" class="mt-0.5 text-[11px] text-n-slate-9">
            {{ pendingSummary }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-n-weak bg-n-surface-1 px-3 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-1 disabled:opacity-60"
        :disabled="transferRequestsLoading"
        @click="fetchTransferRequests"
      >
        <span class="i-lucide-refresh-cw size-3.5" aria-hidden="true" />
        {{ $t('INBOX.REQUESTS.REFRESH') }}
      </button>
    </div>

    <div
      v-if="transferRequestsError"
      class="mb-3 flex items-center justify-between gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-3 px-3 py-2"
    >
      <p class="text-xs text-n-ruby-11">
        Não foi possível atualizar. As solicitações carregadas foram
        preservadas.
      </p>
      <button
        type="button"
        class="shrink-0 text-xs font-semibold text-n-ruby-11 hover:text-n-ruby-12"
        @click="fetchTransferRequests"
      >
        Tentar novamente
      </button>
    </div>

    <div
      class="mb-3 flex gap-1 overflow-x-auto rounded-xl border border-n-weak bg-n-surface-1 p-1"
      role="tablist"
      :aria-label="$t('INBOX.REQUESTS.FILTERS_LABEL')"
    >
      <button
        v-for="filter in statusFilters"
        :key="filter.key"
        type="button"
        role="tab"
        class="flex min-h-8 shrink-0 items-center justify-center rounded-lg px-3 text-xs font-medium transition-colors"
        :class="
          selectedStatus === filter.key
            ? 'bg-[#15603e] text-white shadow-sm'
            : 'text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        :aria-selected="selectedStatus === filter.key"
        @click="selectedStatus = filter.key"
      >
        {{ $t(filter.label) }}
        <span
          v-if="countForStatus(filter.key) > 0"
          class="ml-1.5 inline-flex min-w-4 justify-center rounded-full px-1 text-[10px]"
          :class="
            selectedStatus === filter.key
              ? 'bg-white/20 text-white'
              : 'bg-n-alpha-2 text-n-slate-10'
          "
        >
          {{ countForStatus(filter.key) }}
        </span>
      </button>
    </div>

    <div class="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
      <article
        v-for="request in visibleTransferRequests"
        :key="`${request.kind}-${request.id}`"
        class="rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md"
        :class="
          request.notification_id && !request.notification_read_at
            ? 'border-[#B7E4C7] bg-[#F6FBF7]'
            : 'border-n-weak bg-white'
        "
      >
        <div class="block w-full text-left">
          <div class="mb-3 flex flex-wrap items-center gap-2">
            <span
              class="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
              :class="
                request.kind === 'ownership'
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : request.kind === 'atendimento'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-[#e7f5e9] text-[#15603e] dark:bg-[rgba(21,96,62,0.25)] dark:text-[#86efac]'
              "
            >
              {{
                $t(
                  request.kind === 'ownership'
                    ? 'INBOX.REQUESTS.OWNERSHIP'
                    : request.kind === 'atendimento'
                      ? 'INBOX.REQUESTS.ATTENDANCE'
                      : 'INBOX.REQUESTS.PORTFOLIO'
                )
              }}
            </span>
            <div class="ml-auto flex items-center gap-1.5">
              <button
                v-if="request.notification_id"
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="markingRequestReadId === request.notification_id"
                :aria-label="
                  $t(
                    request.notification_read_at
                      ? 'INBOX.MENU_ITEM.MARK_AS_UNREAD'
                      : 'INBOX.MENU_ITEM.MARK_AS_READ'
                  )
                "
                :title="
                  $t(
                    request.notification_read_at
                      ? 'INBOX.MENU_ITEM.MARK_AS_UNREAD'
                      : 'INBOX.MENU_ITEM.MARK_AS_READ'
                  )
                "
                @click.stop="toggleRequestReadState(request)"
              >
                <span
                  :class="
                    request.notification_read_at
                      ? 'i-lucide-mail-open'
                      : 'i-lucide-mail'
                  "
                  class="size-4"
                  aria-hidden="true"
                />
              </button>
              <button
                v-if="request.notification_id"
                type="button"
                class="inline-flex size-7 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 disabled:opacity-50"
                :disabled="
                  deletingRequestNotificationId === request.notification_id
                "
                :aria-label="$t('INBOX.MENU_ITEM.DELETE')"
                :title="$t('INBOX.MENU_ITEM.DELETE')"
                @click.stop="deleteRequestNotification(request)"
              >
                <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
              </button>
              <span class="text-xs text-n-slate-9">
                {{ formatTransferRequestDate(request.created_at) }}
              </span>
            </div>
          </div>
          <div class="mb-2 flex flex-wrap items-start justify-between gap-2">
            <p
              class="min-w-0 text-sm text-n-slate-12"
              :class="
                request.notification_id && !request.notification_read_at
                  ? 'font-bold'
                  : 'font-normal'
              "
            >
              <span
                v-if="request.notification_id && !request.notification_read_at"
                class="mr-2 inline-block size-2 rounded-full bg-[#15803D] align-middle"
                aria-hidden="true"
              />
              <span v-if="request.contact_name" class="break-words">
                {{ request.contact_name }}
              </span>
              <span v-else>{{ $t('INBOX.REQUESTS.CONVERSATION') }}</span>
              <span class="ml-1 font-normal text-n-slate-9">
                #{{ request.conversation_id }}
              </span>
              <span
                v-if="request.contact_phone"
                class="ml-1 block font-normal text-n-slate-9"
              >
                +{{ request.contact_phone }}
              </span>
            </p>
            <span
              class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold"
              :class="statusClasses[request.status] || statusClasses.pending"
            >
              {{ $t(statusKeys[request.status] || statusKeys.pending) }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10">
            {{
              $t('INBOX.REQUESTS.REQUESTED_BY', {
                name:
                  request.requester?.name ||
                  $t('INBOX.REQUESTS.UNKNOWN_REQUESTER'),
              })
            }}
          </p>
          <div
            v-if="request.kind !== 'ownership'"
            class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-n-surface-2 px-3 py-2 text-xs text-n-slate-11"
          >
            <span class="truncate">
              {{
                request.kind === 'atendimento'
                  ? request.current_assignee?.name ||
                    $t('INBOX.REQUESTS.NO_ASSIGNEE')
                  : request.current_owner?.name || $t('INBOX.REQUESTS.NO_OWNER')
              }}
            </span>
            <span class="i-lucide-arrow-right size-3.5 text-n-slate-9" />
            <span class="truncate font-semibold text-n-slate-12">
              {{
                request.target_assignee?.name ||
                $t('INBOX.REQUESTS.UNKNOWN_TARGET')
              }}
            </span>
            <span
              v-if="request.team_name"
              class="w-full text-[11px] text-n-slate-9"
            >
              {{ $t('INBOX.REQUESTS.TEAM', { name: request.team_name }) }}
            </span>
          </div>
          <p
            v-else
            class="mt-3 rounded-lg bg-n-surface-2 px-3 py-2 text-xs text-n-slate-11"
          >
            Responsável pela carteira:
            {{ request.current_owner?.name || $t('INBOX.REQUESTS.NO_OWNER') }}
            <span v-if="request.inbox_name">
              · Inbox: {{ request.inbox_name }}
            </span>
          </p>
          <p
            v-if="request.justification"
            class="mt-3 line-clamp-3 break-words text-xs leading-relaxed text-n-slate-10"
          >
            <span class="font-semibold text-n-slate-11">
              {{
                request.kind === 'ownership'
                  ? 'Mensagem opcional'
                  : $t('INBOX.REQUESTS.JUSTIFICATION')
              }}:
            </span>
            {{ request.justification }}
          </p>
        </div>

        <button
          type="button"
          class="mt-3 text-xs font-semibold text-[#15603e] hover:underline dark:text-[#86efac]"
          :aria-expanded="
            expandedTransferRequestId === `${request.kind}-${request.id}`
          "
          @click="toggleTransferRequestDetails(request)"
        >
          {{
            expandedTransferRequestId === `${request.kind}-${request.id}`
              ? $t('INBOX.REQUESTS.HIDE_DETAILS')
              : $t('INBOX.REQUESTS.SHOW_DETAILS')
          }}
        </button>

        <div
          v-if="expandedTransferRequestId === `${request.kind}-${request.id}`"
          class="mt-3 max-h-64 space-y-2 overflow-y-auto rounded-lg bg-n-surface-2 p-3 text-xs text-n-slate-11"
        >
          <p class="break-words">
            <span class="font-semibold">
              {{ $t('INBOX.REQUESTS.REQUESTER') }}:
            </span>
            {{
              request.requester?.name || $t('INBOX.REQUESTS.UNKNOWN_REQUESTER')
            }}
          </p>
          <p class="break-words">
            <span class="font-semibold">
              {{
                request.kind === 'ownership'
                  ? 'Responsável pela carteira'
                  : request.kind === 'atendimento'
                    ? $t('INBOX.REQUESTS.CURRENT_ASSIGNEE')
                    : $t('INBOX.REQUESTS.CURRENT_OWNER')
              }}
            </span>
            {{
              request.kind === 'atendimento'
                ? request.current_assignee?.name ||
                  $t('INBOX.REQUESTS.NO_ASSIGNEE')
                : request.current_owner?.name || $t('INBOX.REQUESTS.NO_OWNER')
            }}
          </p>
          <p v-if="request.kind !== 'ownership'" class="break-words">
            <span class="font-semibold">
              {{
                request.kind === 'atendimento'
                  ? $t('INBOX.REQUESTS.NEW_ASSIGNEE')
                  : $t('INBOX.REQUESTS.NEW_OWNER')
              }}
            </span>
            {{
              request.target_assignee?.name ||
              $t('INBOX.REQUESTS.UNKNOWN_TARGET')
            }}
          </p>
          <p v-if="request.team_name" class="break-words">
            <span class="font-semibold">
              {{ $t('INBOX.REQUESTS.TEAM_LABEL') }}:
            </span>
            {{ request.team_name }}
          </p>
          <p
            v-if="request.justification"
            class="whitespace-pre-wrap break-words"
          >
            <span class="font-semibold">
              {{ request.kind === 'ownership' ? 'Mensagem' : 'Justificativa' }}:
            </span>
            {{ request.justification }}
          </p>
        </div>

        <p v-if="request.decided_at" class="mt-2 text-xs text-n-slate-9">
          {{
            $t('INBOX.REQUESTS.DECIDED_AT', {
              date: formatTransferRequestDate(request.decided_at),
            })
          }}
        </p>

        <p v-if="request.decided_by?.name" class="mt-1 text-xs text-n-slate-9">
          {{
            $t('INBOX.REQUESTS.DECIDED_BY', { name: request.decided_by.name })
          }}
        </p>

        <p
          v-if="request.status === 'rejected' && request.rejection_reason"
          class="mt-2 break-words text-xs text-n-slate-10"
        >
          <span class="font-semibold">
            {{ $t('INBOX.REQUESTS.REJECTION_REASON') }}:
          </span>
          {{ request.rejection_reason }}
        </p>

        <div
          v-if="request.status === 'pending'"
          class="mt-4 flex flex-col gap-2 sm:flex-row"
        >
          <button
            type="button"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-lg bg-[#15603e] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#0f4b30] disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="
              decidingTransferRequestId === `${request.kind}-${request.id}`
            "
            @click="approveTransferRequest(request)"
          >
            {{
              decidingTransferRequestId === `${request.kind}-${request.id}`
                ? $t('INBOX.REQUESTS.PROCESSING')
                : $t('INBOX.REQUESTS.APPROVE')
            }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-n-weak px-4 text-sm font-semibold text-n-slate-11 transition-colors hover:border-n-ruby-6 hover:bg-n-ruby-2 hover:text-n-ruby-11 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="
              decidingTransferRequestId === `${request.kind}-${request.id}`
            "
            @click="openRejectTransferRequestModal(request)"
          >
            {{ $t('INBOX.REQUESTS.REJECT') }}
          </button>
        </div>
      </article>
      <div
        v-if="visibleTransferRequests.length === 0 && !transferRequestsLoading"
        class="rounded-xl border border-dashed border-n-weak bg-n-surface-1 px-4 py-7 text-center"
        role="status"
      >
        <span
          class="i-lucide-inbox mb-2 inline-block size-6 text-n-slate-8"
          aria-hidden="true"
        />
        <p class="text-sm font-medium text-n-slate-11">
          {{ $t('INBOX.REQUESTS.NO_STATUS_REQUESTS') }}
        </p>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="rejectionTransferRequest"
      data-testid="portfolio-transfer-rejection-modal"
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      @click.self="closeRejectTransferRequestModal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-transfer-rejection-title"
        class="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-n-solid-1 p-5 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="portfolio-transfer-rejection-title"
              class="text-base font-semibold text-n-slate-12"
            >
              Recusar {{ labelFor(rejectionTransferRequest).toLowerCase() }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              A solicitação da conversa #{{
                rejectionTransferRequest.conversation_id
              }}
              será recusada.
            </p>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Fechar"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="closeRejectTransferRequestModal"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <label
          class="mt-5 block text-sm font-medium text-n-slate-12"
          for="portfolio-transfer-rejection-reason"
        >
          Motivo da recusa
          <span class="font-normal text-n-slate-9">(opcional)</span>
        </label>
        <textarea
          id="portfolio-transfer-rejection-reason"
          v-model="rejectionReason"
          rows="4"
          maxlength="500"
          placeholder="Explique o motivo da recusa, se desejar."
          class="mt-2 w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-8 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="Boolean(decidingTransferRequestId)"
        />

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="closeRejectTransferRequestModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-9 rounded-lg bg-n-ruby-9 px-4 text-sm font-semibold text-white hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="rejectTransferRequest"
          >
            {{ decidingTransferRequestId ? 'Recusando…' : 'Confirmar recusa' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
